import './BreathingAnimation.css';
import { useCallback, useEffect, useMemo, useRef } from 'react';

/*
  Animation de respiration — portage exact de la grammaire des vidéos B2B.

  Correspondances avec le moteur B2B (engine.py), toutes converties de pixels
  (cadre 1920×1080, cercle de rayon 168→392) en unités du viewBox (100 unités,
  cercle de rayon 30→44), soit un facteur 1/8,9 :

  - contour de base : noyau σ 2,4→3,6 px + nappe large σ 21,6 px  → .hb-base + .hb-halo
  - trace           : noyau σ 2 px à 0,92 + lueur σ 7,5 px à 0,24 → .hb-trail + .hb-trail-glow
  - renfort derriere la tete : exp(-((j-q)/34)²)·0,55 sur 2048 pas → .hb-comet
  - tete            : blanc σ 7 px à 0,95, couleur σ 15 px à 0,55 et σ 34 px à 0,20
                      → trois disques à dégradé radial, sans filtre SVG
  - resorption      : leftover = max(0, 1 − age/0,30), exactement comme le B2B
  - onde de fin de cycle : copie homothétique, donc le triangle garde ses pointes

  La tete n'est pas un disque plein a bord net : c'est un point lumineux a
  decroissance douce, qui ne deborde pas du trait. C'est ce qui distingue le
  rendu B2B d'un curseur "bille".

  Aucun filtre SVG n'est utilisé : les lueurs sont des dégradés radiaux et des
  traits larges à faible opacité. Rendu identique à toutes les tailles, et rien
  ne coûte de filtre par image sur mobile.

  Aucune unité d'écran non plus (pas de vector-effect) : les épaisseurs sont en
  unités du viewBox, comme les longueurs de pointillés, sinon la trace se
  décale du curseur dès que le widget change de taille.
*/

const CX = 50;
const CY = 50;
const RESORB = 0.3;        // résorption de la trace, en secondes — valeur B2B
const LABEL_FADE = 0.38;   // fondu du libellé au changement de texte
const WAVE_LIFE = 2.6;     // durée de vie de l'onde de fin de cycle
const SIDE_AMP = 14;       // décalage latéral de la respiration alternée
const COMET = 0.055;       // longueur du renfort derrière la tête, en fraction du contour
const LABEL_SIZE = 8.2;    // taille du libellé, en unités du viewBox — proportion B2B

function clamp01(value) {
  return value < 0 ? 0 : value > 1 ? 1 : value;
}

function smoothstep(t) {
  const p = clamp01(t);
  return p * p * (3 - 2 * p);
}

const EASE = {
  cos: (t) => 0.5 - 0.5 * Math.cos(Math.PI * clamp01(t)),
  out: (t) => 1 - Math.pow(1 - clamp01(t), 2.2),
  long: (t) => 1 - Math.pow(1 - clamp01(t), 1.55),
  hold: () => 1,
  fast: (t) => 0.5 - 0.5 * Math.cos(2 * Math.PI * 6 * clamp01(t)),
  slow: (t) => 0.5 - 0.5 * Math.cos(2 * Math.PI * 2 * clamp01(t)),
};

/* ---------------------------------------------------------------- contours
   Le parametre k met le trace a l'echelle : 1 pour le SVG (unites du viewBox),
   0,01 pour le clip du verre depoli, exprime en fraction de la boite. */

const TRI_OY = 13.8;   // descente optique du triangle — CONSTANTE, comme le B2B

function circlePath(r, k = 1) {
  const cx = (CX * k).toFixed(4);
  const rr = (r * k).toFixed(4);
  const top = ((CY - r) * k).toFixed(4);
  const bottom = ((CY + r) * k).toFixed(4);
  return `M ${cx} ${top} A ${rr} ${rr} 0 0 1 ${cx} ${bottom} A ${rr} ${rr} 0 0 1 ${cx} ${top} Z`;
}

function squarePath(half, k = 1) {
  /* Le trace demarre au MILIEU de l'arrondi haut-gauche et tourne dans le sens
     horaire. Chaque arrondi etant coupe en deux, un quart du perimetre vaut
     exactement demi-arrondi + un cote + demi-arrondi : une phase = un cote. */
  const kk = half * 0.17;
  const f = (v) => (v * k).toFixed(4);
  const l = CX - half;
  const r = CX + half;
  const t = CY - half;
  const b = CY + half;
  const h = kk * 0.70710678;
  const sx = f(l + kk - h);
  const sy = f(t + kk - h);
  const a = `A ${f(kk)} ${f(kk)} 0 0 1`;
  return `M ${sx} ${sy} ${a} ${f(l + kk)} ${f(t)} L ${f(r - kk)} ${f(t)} ${a} ${f(r)} ${f(t + kk)} L ${f(r)} ${f(b - kk)} ${a} ${f(r - kk)} ${f(b)} L ${f(l + kk)} ${f(b)} ${a} ${f(l)} ${f(b - kk)} L ${f(l)} ${f(t + kk)} ${a} ${sx} ${sy} Z`;
}

function trianglePath(R, k = 1) {
  /* Le centre de gravite est FIXE : il ne depend pas de R. Sans cela, agrandir
     le triangle le fait aussi descendre, et l'onde de fin de cycle laisse deux
     fois plus de jeu sous la base que sur les cotes inclines. */
  const f = (v) => (v * k).toFixed(4);
  const cy = CY + TRI_OY;
  const dx = R * 0.8660254;
  return `M ${f(CX)} ${f(cy - R)} L ${f(CX + dx)} ${f(cy + R / 2)} L ${f(CX - dx)} ${f(cy + R / 2)} Z`;
}

/* ---------------------------------------------------------------- formes */

const SHAPES = {
  circle: { min: 33, max: 48, extent: 1, build: circlePath, labelDY: 0 },
  square: { min: 35, max: 42, extent: 1.4142, build: squarePath, labelDY: 0 },
  triangle: { min: 48, max: 56, extent: 2, build: trianglePath, labelDY: 11.5 },
};

function labelRoom(kind, size) {
  if (kind === 'circle') return 2 * size * 0.78;
  if (kind === 'square') return 2 * size * 0.8;
  /* Triangle : demi-largeur a la hauteur du libelle = (decalage + R) / racine(3) */
  return 2 * ((SHAPES.triangle.labelDY + size) / 1.7320508) * 0.62;
}

/* ---------------------------------------------------------------- techniques
   Les libellés et les durées sont ceux du B2C. Un retour à la ligne (\n) coupe
   un libellé trop long en deux lignes centrées.

   tracer 'phase'   : un tour complet du contour par phase (cercle, carré)
   tracer 'segment' : un côté par phase, le tour se boucle sur le cycle (triangle) */

export const ANIMATIONS = {
  1: {
    shape: 'circle',
    tracer: 'phase',
    phases: [
      { duration: 6, from: 0, to: 1, ease: 'cos', label: 'Inspirez' },
      { duration: 6, from: 1, to: 0, ease: 'cos', label: 'Expirez' },
    ],
  },
  2: {
    shape: 'square',
    tracer: 'segment',
    phases: [
      { duration: 4, from: 0, to: 1, ease: 'cos', label: 'Inspirez' },
      { duration: 4, from: 1, to: 1, ease: 'hold', label: 'Pause' },
      { duration: 4, from: 1, to: 0, ease: 'cos', label: 'Expirez' },
      { duration: 4, from: 0, to: 0, ease: 'hold', label: 'Pause' },
    ],
  },
  3: {
    shape: 'circle',
    tracer: 'phase',
    phases: [
      { duration: 3.5, from: 0, to: 0.72, ease: 'out', label: 'Grande\ninspiration' },
      { duration: 1, from: 0.72, to: 0.72, ease: 'hold', label: 'Pause' },
      { duration: 2, from: 0.72, to: 1, ease: 'out', label: 'Petite\ninspiration' },
      { duration: 8.5, from: 1, to: 0, ease: 'long', label: 'Longue\nexpiration' },
    ],
  },
  4: {
    shape: 'circle',
    tracer: 'phase',
    phases: [
      { duration: 3, from: 0, to: 1, ease: 'out', label: 'Inspirez' },
      { duration: 7, from: 1, to: 0, ease: 'long', label: 'Expirez' },
    ],
  },
  5: {
    shape: 'circle',
    tracer: 'phase',
    phases: [
      { duration: 4, from: 0, to: 1, ease: 'cos', label: 'Inspirez' },
      { duration: 7, from: 1, to: 1, ease: 'hold', label: 'Pause' },
      { duration: 8, from: 1, to: 0, ease: 'long', label: 'Expirez' },
    ],
  },
  6: {
    shape: 'circle',
    tracer: 'phase',
    sides: [-1, 1, 1, -1],
    phases: [
      { duration: 3, from: 0, to: 1, ease: 'cos', label: 'Inspirez\ngauche' },
      { duration: 3, from: 1, to: 0, ease: 'cos', label: 'Expirez\ndroite' },
      { duration: 3, from: 0, to: 1, ease: 'cos', label: 'Inspirez\ndroite' },
      { duration: 3, from: 1, to: 0, ease: 'cos', label: 'Expirez\ngauche' },
    ],
  },
  7: {
    shape: 'triangle',
    tracer: 'segment',
    phases: [
      { duration: 4, from: 0, to: 1, ease: 'cos', label: 'Inspirez' },
      { duration: 4, from: 1, to: 1, ease: 'hold', label: 'Pause' },
      { duration: 8, from: 1, to: 0, ease: 'long', label: 'Longue\nexpiration' },
    ],
  },
  8: {
    shape: 'circle',
    tracer: 'phase',
    phases: [
      { duration: 10, from: 0, to: 1, ease: 'fast', label: 'Respiration\nrapide' },
      { duration: 10, from: 0, to: 0.6, ease: 'slow', label: 'Respiration\nnormale' },
    ],
  },
  9: {
    shape: 'circle',
    tracer: 'phase',
    phases: [
      { duration: 8, from: 0, to: 1, ease: 'cos', label: 'Inspirez' },
      { duration: 8, from: 1, to: 0, ease: 'cos', label: 'Expirez' },
    ],
  },
  10: {
    shape: 'circle',
    tracer: 'phase',
    phases: [
      { duration: 4, from: 0, to: 1, ease: 'cos', label: 'Inspirez' },
      { duration: 16, from: 1, to: 1, ease: 'hold', label: 'Pause' },
      { duration: 8, from: 1, to: 0, ease: 'long', label: 'Longue\nexpiration' },
    ],
  },
  11: {
    shape: 'circle',
    tracer: 'phase',
    phases: [
      { duration: 5, from: 0, to: 1, ease: 'cos', label: 'Inspirez' },
      { duration: 5, from: 1, to: 0, ease: 'cos', label: 'Expirez' },
      { duration: 5, from: 0, to: 0, ease: 'hold', label: 'Pause' },
    ],
  },
};

/* Segments de libellé : les phases consécutives portant le même texte fusionnent,
   pour que le fondu ne joue qu'aux vrais changements. */
function buildMeta(config) {
  const phases = config.phases;
  const cycle = phases.reduce((sum, phase) => sum + phase.duration, 0);
  const starts = [];
  const segments = [];
  let acc = 0;
  phases.forEach((phase) => {
    starts.push(acc);
    const last = segments[segments.length - 1];
    if (last && last.label === phase.label) last.end = acc + phase.duration;
    else segments.push({ start: acc, end: acc + phase.duration, label: phase.label });
    acc += phase.duration;
  });
  return { cycle, starts, segments };
}

function sideAt(config, meta, time) {
  if (!config.sides) return 0;
  const tt = ((time % meta.cycle) + meta.cycle) % meta.cycle;
  const width = 0.75;
  let value = config.sides[0];
  meta.starts.forEach((start, index) => {
    const bound = start + config.phases[index].duration;
    const next = config.sides[(index + 1) % config.sides.length];
    const delta = next - config.sides[index];
    if (delta) value += delta * smoothstep((tt - (bound - width)) / (2 * width));
  });
  return value;
}

/* ---------------------------------------------------------------- composant */

const BreathingAnimation = ({ technique, isPlaying, currentTime, audioRef, large = false }) => {
  const config = ANIMATIONS[technique] || ANIMATIONS[1];
  const meta = useMemo(() => buildMeta(config), [config]);
  const shape = SHAPES[config.shape];
  const uid = `hb-${technique ?? 'x'}`;

  const rootRef = useRef(null);
  const groupRef = useRef(null);
  const clipRef = useRef(null);
  const haloRef = useRef(null);
  const baseRef = useRef(null);
  const leftoverRef = useRef(null);
  const trailGlowRef = useRef(null);
  const trailRef = useRef(null);
  const cometRef = useRef(null);
  const waveRef = useRef(null);
  const headWideRef = useRef(null);
  const headMidRef = useRef(null);
  const headCoreRef = useRef(null);
  const labelRef = useRef(null);
  const lineARef = useRef(null);
  const lineBRef = useRef(null);

  const lastLabelRef = useRef('');
  const ratioRef = useRef(5);    // largeur de la ligne la plus large, en cadratins
  const unitRef = useRef(1.2);   // pixels par unité de viewBox, mesuré sur la scène
  const baseFontRef = useRef(0); // taille de rastérisation du libellé, fixe entre deux redimensionnements
  const targetTimeRef = useRef(Math.max(0, Number(currentTime) || 0));
  const targetStampRef = useRef(typeof performance !== 'undefined' ? performance.now() : 0);
  const visualTimeRef = useRef(targetTimeRef.current);
  const isPlayingRef = useRef(isPlaying);
  const rafRef = useRef(0);

  useEffect(() => { isPlayingRef.current = isPlaying; }, [isPlaying]);

  /* Largeur réelle du libellé, mesurée une fois à chaque changement de texte :
     l'ajustement est exact quelle que soit la police finalement chargée. */
  const measureRatio = useCallback(() => {
    const node = labelRef.current;
    if (!node) return 5;
    const probe = 100;
    const previous = node.style.fontSize;
    node.style.fontSize = `${probe}px`;
    const a = lineARef.current ? lineARef.current.getBoundingClientRect().width : 0;
    const b = lineBRef.current ? lineBRef.current.getBoundingClientRect().width : 0;
    node.style.fontSize = previous;
    return Math.max(a, b, 1) / probe;
  }, []);

  const draw = useCallback((time) => {
    const trail = trailRef.current;
    if (!trail) return;

    const running = isPlayingRef.current;
    const tt = ((time % meta.cycle) + meta.cycle) % meta.cycle;

    let index = meta.starts.length - 1;
    for (let i = 0; i < meta.starts.length; i += 1) {
      if (tt < meta.starts[i] + config.phases[i].duration) { index = i; break; }
    }
    const phase = config.phases[index];
    const elapsed = tt - meta.starts[index];
    const p = clamp01(elapsed / phase.duration);
    const value = phase.from + (phase.to - phase.from) * EASE[phase.ease](p);

    const size = shape.min + (shape.max - shape.min) * value;
    const d = shape.build(size);
    const lum = 0.3 + 0.7 * value;        // luminosité globale, comme le B2B
    const glow = 0.5 + 0.5 * value;       // brillance de la trace, comme le B2B

    if (clipRef.current) clipRef.current.setAttribute('d', shape.build(size, 0.01));
    if (haloRef.current) haloRef.current.setAttribute('d', d);
    if (baseRef.current) baseRef.current.setAttribute('d', d);
    if (leftoverRef.current) leftoverRef.current.setAttribute('d', d);
    if (trailGlowRef.current) trailGlowRef.current.setAttribute('d', d);
    trail.setAttribute('d', d);
    if (cometRef.current) cometRef.current.setAttribute('d', d);

    if (haloRef.current) haloRef.current.setAttribute('opacity', (0.05 * lum).toFixed(3));
    if (baseRef.current) baseRef.current.setAttribute('opacity', (0.42 * lum).toFixed(3));

    const head = config.tracer === 'segment' ? (index + p) / config.phases.length : p;
    const length = trail.getTotalLength();
    const headLen = head * length;
    const dash = `${headLen.toFixed(3)} ${(length + 1).toFixed(3)}`;

    trail.setAttribute('stroke-dasharray', dash);
    trail.setAttribute('opacity', running ? glow.toFixed(3) : '0');
    if (trailGlowRef.current) {
      trailGlowRef.current.setAttribute('stroke-dasharray', dash);
      trailGlowRef.current.setAttribute('opacity', running ? (0.2 * glow).toFixed(3) : '0');
    }

    /* Renfort juste derrière la tête : le trait est plus dense sur les derniers
       pourcents parcourus, ce qui donne à la tête sa traîne de comète. */
    if (cometRef.current) {
      const seg = Math.min(COMET * length, headLen);
      cometRef.current.setAttribute('stroke-dasharray', `${seg.toFixed(3)} ${(length + 1).toFixed(3)}`);
      cometRef.current.setAttribute('stroke-dashoffset', (seg - headLen).toFixed(3));
      cometRef.current.setAttribute('opacity', running ? (0.5 * glow).toFixed(3) : '0');
    }

    /* Résorption : la portion non encore parcourue s'éteint en 0,3 s pendant que
       la phase suivante repart. Aucune coupure visible. */
    const resorbFrom = config.tracer === 'segment' ? tt : elapsed;
    const leftover = running ? Math.max(0, 1 - resorbFrom / RESORB) : 0;
    if (leftoverRef.current) leftoverRef.current.setAttribute('opacity', (leftover * glow).toFixed(3));

    const point = trail.getPointAtLength(headLen);
    const side = sideAt(config, meta, time) * SIDE_AMP;
    if (groupRef.current) groupRef.current.setAttribute('transform', `translate(${side.toFixed(2)} 0)`);

    const hg = 0.62 + 0.38 * value;
    const px = point.x.toFixed(2);
    const py = point.y.toFixed(2);
    const heads = [
      [headWideRef.current, 0.10 * hg],
      [headMidRef.current, 0.28 * hg],
      [headCoreRef.current, 0.95 * hg],
    ];
    heads.forEach(([node, amp]) => {
      if (!node) return;
      node.setAttribute('cx', px);
      node.setAttribute('cy', py);
      node.setAttribute('opacity', running ? amp.toFixed(3) : '0');
    });

    if (waveRef.current) {
      const tau = tt;
      if (running && tau < WAVE_LIFE) {
        const amp = Math.min(tau / 0.28, 1) * Math.pow(1 - tau / WAVE_LIFE, 2) * 0.4;
        const extra = (0.04 + tau * 0.34) * shape.max / shape.extent;
        waveRef.current.setAttribute('d', shape.build(size + extra));
        waveRef.current.setAttribute('opacity', amp.toFixed(3));
      } else {
        waveRef.current.setAttribute('opacity', '0');
      }
    }

    const segment = meta.segments.find((item) => tt < item.end) || meta.segments[0];
    const fade = Math.min((tt - segment.start) / LABEL_FADE, (segment.end - tt) / LABEL_FADE, 1);
    const alpha = running ? Math.pow(Math.max(0, fade), 1.4) : 0.45;

    if (labelRef.current) {
      if (lastLabelRef.current !== segment.label) {
        const lines = segment.label.split('\n');
        if (lineARef.current) lineARef.current.textContent = lines[0] || '';
        if (lineBRef.current) lineBRef.current.textContent = lines[1] || '';
        lastLabelRef.current = segment.label;
        ratioRef.current = measureRatio();
      }
      /* La taille de police ne change JAMAIS pendant l'animation : le navigateur
         arrondit les avances de glyphes au pixel, ce qui faisait sauter le texte.
         L'echelle passe par un transform, continu et compose par le GPU. Le texte
         est rasterise a sa taille maximale et seulement reduit : jamais flou. */
      const unit = unitRef.current;
      const room = labelRoom(config.shape, size) * unit;
      const target = Math.min(LABEL_SIZE * (0.82 + 0.18 * value) * unit, room / ratioRef.current);
      const k = baseFontRef.current > 0 ? target / baseFontRef.current : 1;
      labelRef.current.style.opacity = alpha.toFixed(3);
      labelRef.current.style.transform = `translate(-50%, -50%) translate(${(side * unit).toFixed(2)}px, ${(shape.labelDY * unit).toFixed(2)}px) scale(${k.toFixed(4)})`;
    }
  }, [config, measureRatio, meta, shape]);

  useEffect(() => {
    const node = rootRef.current;
    if (!node) return undefined;
    const measure = () => {
      const width = node.getBoundingClientRect().width;
      if (width <= 0) return;
      unitRef.current = width / 100;
      baseFontRef.current = LABEL_SIZE * unitRef.current;
      if (labelRef.current) labelRef.current.style.fontSize = `${baseFontRef.current.toFixed(2)}px`;
      ratioRef.current = measureRatio();
    };
    measure();
    if (typeof ResizeObserver === 'undefined') return undefined;
    const observer = new ResizeObserver(measure);
    observer.observe(node);
    return () => observer.disconnect();
  }, [measureRatio]);

  useEffect(() => {
    const audio = audioRef?.current;
    const ready = audio?.src && audio.readyState >= 1;
    const next = ready ? Math.max(0, Number(audio.currentTime) || 0) : Math.max(0, Number(currentTime) || 0);
    targetTimeRef.current = next;
    targetStampRef.current = performance.now();
    if (!isPlaying || Math.abs(next - visualTimeRef.current) > 0.35) {
      visualTimeRef.current = next;
      draw(next);
    }
  }, [audioRef, currentTime, draw, isPlaying]);

  useEffect(() => {
    draw(visualTimeRef.current);
    if (!isPlaying) return undefined;
    const loop = () => {
      const age = (performance.now() - targetStampRef.current) / 1000;
      const audio = audioRef?.current;
      const ready = audio?.src && audio.readyState >= 1;
      const next = ready ? Math.max(0, Number(audio.currentTime) || 0) : targetTimeRef.current + Math.min(age, 0.12);
      visualTimeRef.current = next;
      draw(next);
      rafRef.current = requestAnimationFrame(loop);
    };
    rafRef.current = requestAnimationFrame(loop);
    return () => cancelAnimationFrame(rafRef.current);
  }, [audioRef, draw, isPlaying]);

  return (
    <div className={`breathing-animation-compact${large ? ' breathing-animation-compact--large' : ''}`}>
      <div
        ref={rootRef}
        className={`hb-stage hb-stage--${config.shape}${isPlaying ? '' : ' is-idle'}`}
      >
        <div
          className="hb-frost"
          style={{ clipPath: `url(#${uid}-clip)`, WebkitClipPath: `url(#${uid}-clip)` }}
        />

        <svg
          className="hb-svg"
          viewBox="0 0 100 100"
          preserveAspectRatio="xMidYMid meet"
          aria-hidden="true"
        >
          <defs>
            <linearGradient id={`${uid}-line`} x1="0" y1="0" x2="1" y2="0">
              <stop offset="0.2" className="hb-stop-1" />
              <stop offset="0.8" className="hb-stop-2" />
            </linearGradient>
            <clipPath id={`${uid}-clip`} clipPathUnits="objectBoundingBox">
              <path ref={clipRef} />
            </clipPath>
            <radialGradient id={`${uid}-glow`}>
              <stop offset="0" className="hb-stop-glow" stopOpacity="0.8" />
              <stop offset="0.3" className="hb-stop-glow" stopOpacity="0.34" />
              <stop offset="0.62" className="hb-stop-glow" stopOpacity="0.08" />
              <stop offset="1" className="hb-stop-glow" stopOpacity="0" />
            </radialGradient>
            <radialGradient id={`${uid}-core`}>
              <stop offset="0" className="hb-stop-head" stopOpacity="1" />
              <stop offset="0.34" className="hb-stop-head" stopOpacity="0.72" />
              <stop offset="0.66" className="hb-stop-head" stopOpacity="0.2" />
              <stop offset="1" className="hb-stop-head" stopOpacity="0" />
            </radialGradient>
          </defs>
          <g ref={groupRef}>
            <path ref={waveRef} className="hb-wave" stroke={`url(#${uid}-line)`} opacity="0" />
            <path ref={haloRef} className="hb-halo" stroke={`url(#${uid}-line)`} opacity="0" />
            <path ref={baseRef} className="hb-base" stroke={`url(#${uid}-line)`} opacity="0" />
            <path ref={leftoverRef} className="hb-leftover" stroke={`url(#${uid}-line)`} opacity="0" />
            <path ref={trailGlowRef} className="hb-trail-glow" stroke={`url(#${uid}-line)`} opacity="0" />
            <path ref={trailRef} className="hb-trail" stroke={`url(#${uid}-line)`} opacity="0" />
            <path ref={cometRef} className="hb-comet" stroke={`url(#${uid}-line)`} opacity="0" />
            <circle ref={headWideRef} className="hb-head" r="5.6" fill={`url(#${uid}-glow)`} opacity="0" />
            <circle ref={headMidRef} className="hb-head" r="2.2" fill={`url(#${uid}-glow)`} opacity="0" />
            <circle ref={headCoreRef} className="hb-head" r="1.15" fill={`url(#${uid}-core)`} opacity="0" />
          </g>
        </svg>

        <div
          ref={labelRef}
          className="hb-label"
        >
          <span
            ref={lineARef}
            className="hb-label__line"
          />
          <span
            ref={lineBRef}
            className="hb-label__line"
          />
        </div>
      </div>
    </div>
  );
};

export default BreathingAnimation;